/**
 * Breadcrumb Navigation component
 * Displays breadcrumb trail based on current route
 */

import { useLocation, Link } from 'react-router-dom'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { IconHome } from '@tabler/icons-react'

interface RouteConfig {
  path: string
  label: string
  parent?: string
}

const routeMap: Record<string, RouteConfig> = {
  '/': {
    path: '/',
    label: 'Dashboard',
  },
  '/assets': {
    path: '/assets',
    label: 'Tài sản',
    parent: '/',
  },
  '/prices': {
    path: '/prices',
    label: 'Lịch sử Giá',
    parent: '/',
  },
  '/reports': {
    path: '/reports',
    label: 'Báo cáo',
    parent: '/',
  },
  '/expectations': {
    path: '/expectations',
    label: 'Kỳ vọng',
    parent: '/',
  },
  '/price-expectation': {
    path: '/price-expectation',
    label: 'Kỳ vọng Giá',
    parent: '/',
  },
  '/settings': {
    path: '/settings',
    label: 'Cài đặt',
    parent: '/',
  },
}

export function BreadcrumbNavigation() {
  const location = useLocation()
  const pathname = location.pathname

  // Build breadcrumb items
  const buildBreadcrumbs = () => {
    const items: Array<{ path: string; label: string }> = []
    
    // Always start with Home
    items.push({ path: '/', label: 'Trang chủ' })
    
    // Get current route config
    const currentRoute = routeMap[pathname]
    
    if (currentRoute) {
      // If current route has a parent, add it (but skip if parent is home since we already have it)
      if (currentRoute.parent && currentRoute.parent !== '/') {
        const parentRoute = routeMap[currentRoute.parent]
        if (parentRoute) {
          items.push({ path: parentRoute.path, label: parentRoute.label })
        }
      }
      
      // Add current route (don't add if it's home)
      if (pathname !== '/') {
        items.push({ path: currentRoute.path, label: currentRoute.label })
      }
    } else {
      // If route not found in map, just show the pathname
      if (pathname !== '/') {
        items.push({ path: pathname, label: pathname })
      }
    }
    
    return items
  }

  const breadcrumbItems = buildBreadcrumbs()

  // Don't show breadcrumb on home page
  if (pathname === '/') {
    return null
  }

  return (
    <Breadcrumb>
      <BreadcrumbList className="flex-wrap">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1
          
          return (
            <div key={`${item.path}-${index}`} className="flex items-center">
              {index === 0 ? (
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to={item.path} className="flex items-center gap-1">
                      <IconHome className="h-4 w-4" stroke={1.5} />
                      <span className="sr-only md:not-sr-only">{item.label}</span>
                    </Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
              ) : (
                <>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {isLast ? (
                      <BreadcrumbPage className="truncate max-w-[120px] md:max-w-none">
                        {item.label}
                      </BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={item.path} className="truncate max-w-[100px] md:max-w-none">
                          {item.label}
                        </Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </>
              )}
            </div>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

